import { startsWord } from './boundaries'

export type SuggestKind = 'project' | 'section' | 'tag'

/** Das Zeichen, hinter dem der Caret gerade sucht — und was bisher dahinter getippt ist. */
export interface SuggestTrigger {
  kind: SuggestKind
  /** Stelle des Zeichens (`#`, `/`, `@`). */
  start: number
  /** Ende des Worts, in dem der Caret steht — bis hierhin ersetzt das Übernehmen. */
  end: number
  /** Getippt zwischen Zeichen und Caret. */
  query: string
}

const KINDS: Record<string, SuggestKind> = { '#': 'project', '/': 'section', '@': 'tag' }
const CHARS: Record<SuggestKind, string> = { project: '#', section: '/', tag: '@' }

export const triggerChar = (kind: SuggestKind) => CHARS[kind]

/**
 * **Sucht der Caret gerade nach einem Projekt, Abschnitt oder Tag?** (#234, Schritt C)
 *
 * Das nächste `#`, `/` oder `@` vor dem Caret, am Wortanfang (`max@example.com` nicht). Ein Leerzeichen
 * beendet die Suche — außer ein Name beginnt so („#Basketball S“). `namesOf` liefert die Namen je Art.
 */
export function activeTrigger(text: string, caret: number, namesOf: (kind: SuggestKind) => string[]): SuggestTrigger | null {
  for (let i = caret - 1; i >= 0; i--) {
    const char = text[i]!
    if (char === '\n') return null
    const kind = KINDS[char]
    if (!kind || !startsWord(text, i)) continue
    const query = text.slice(i + 1, caret)
    const end = caret + (/^\S*/u.exec(text.slice(caret))?.[0].length ?? 0)
    if (!/\s/u.test(query)) return { kind, start: i, end, query }
    const lower = query.toLowerCase()
    return namesOf(kind).some(name => name.toLowerCase().startsWith(lower)) ? { kind, start: i, end, query } : null
  }
  return null
}

/**
 * Was zur Suche passt, ohne Groß/klein: Treffer am Anfang vor Treffern am Wortanfang vor Treffern
 * mittendrin; innerhalb gleich gut bleibt die Reihenfolge.
 */
export function rankByQuery<T>(items: T[], query: string, nameOf: (item: T) => string): T[] {
  const q = query.trim().toLowerCase()
  if (!q) return items
  const score = (name: string) => {
    const lower = name.toLowerCase()
    if (lower.startsWith(q)) return 0
    if (lower.includes(` ${q}`)) return 1
    return lower.includes(q) ? 2 : -1
  }
  return items
    .map((item, index) => ({ item, index, score: score(nameOf(item)) }))
    .filter(entry => entry.score >= 0)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map(entry => entry.item)
}

/**
 * **Übernehmen**: ersetzt das Getippte durch den vollen Namen, ein Leerzeichen dahinter (steht schon
 * eins da, bleibt es bei dem). Liefert den neuen Text und die Caret-Stelle hinter dem Leerzeichen.
 */
export function applySuggestion(text: string, trigger: SuggestTrigger, name: string): { text: string; caret: number } {
  const inserted = `${CHARS[trigger.kind]}${name}`
  const after = text.slice(trigger.end)
  const next = after.startsWith(' ') ? `${text.slice(0, trigger.start)}${inserted}${after}` : `${text.slice(0, trigger.start)}${inserted} ${after}`
  return { text: next, caret: trigger.start + inserted.length + 1 }
}
